import { writeVirtualResourcesToDataDir } from '@/features/virtual-resource/persistence'
import { buildProjectResourceObjectName, uploadMaterialFile, uploadMaterialFiles } from '@/features/material-library/upload'
import { appendMaterialResourceMapping } from '@/features/material-library/resourceMapping'
import { resolveFontDisplayNameFromAsset } from '@/features/project-font/runtime'
import {
  FONT_DISPLAY_NAME_LINK_INDEX,
  FONT_PREVIEW_MD5_LINK_INDEX,
} from '@/features/project-font/library'
import { uploadVirtualResourcesJsonIfConfigured } from '@/control/virtualResourceCloud'
import { uploadVirtualResourceRootsToOss } from '@/features/project-cloud/ossSave'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import type MainProject from '@/model/MainProject'
import type DataFileMap from '@/model/Resource/DataFileMap'
import { buildDefaultVirtualResourceRoot, getResourceCategoryRule } from '@/features/virtual-resource/rules'
import {
  applySkinPackageToProject,
  applySkinResources,
  parseSkinOuiPackage,
  type SkinApplyResult,
} from '@/features/skin-loader/runtime'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { validateImportedProjectResourcePaths } from '@/features/project-mall/runtime'

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'bmp', 'svg'])
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov', 'm4v'])
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'])
const FONT_EXTENSIONS = new Set(['ttf', 'otf', 'woff', 'woff2'])
const OUI_EXTENSIONS = new Set(['oui'])
const FONT_PREVIEW_SIZE = 512
const BATCH_HASH_PROGRESS_END = 30
const BATCH_UPLOAD_PROGRESS_END = 90
const BATCH_QUEUE_PROGRESS = 92

export type LocalResourceApplyProgressStage =
  | 'parsing'
  | 'hashing'
  | 'uploading'
  | 'queue'
  | 'persisting'
  | 'completed'

export type LocalResourceApplyProgress = {
  stage: LocalResourceApplyProgressStage
  percent: number
  detail: string
}

export type LocalResourceApplyProgressCallback = (progress: LocalResourceApplyProgress) => void

function sanitizeFileName(name: string): string {
  return name.trim().replace(/[\\/:*?"<>|]/g, '_')
}

function getFileExtension(name: string): string {
  return name.includes('.') ? name.split('.').pop()!.toLowerCase() : ''
}

function getProjectVersion(project: MainProject): string {
  const version =
    project.projectHeader.Version || project.projectConstant.gameVersion || project.dataVer || 1
  return String(version)
}

function getProjectGuid(project: MainProject): string {
  return project.projectHeader.ID?.trim() ?? ''
}

function emitLocalApplyProgress(
  callback: LocalResourceApplyProgressCallback | undefined,
  progress: LocalResourceApplyProgress,
): void {
  callback?.({
    ...progress,
    percent: Math.max(0, Math.min(100, Math.round(progress.percent))),
  })
}

function mapBatchUploadProgress(stage: LocalResourceApplyProgressStage, percent: number): number {
  const safePercent = Math.max(0, Math.min(100, percent))
  if (stage === 'hashing') return (safePercent / 100) * BATCH_HASH_PROGRESS_END
  if (stage === 'uploading') {
    return (safePercent / 100) * BATCH_UPLOAD_PROGRESS_END
  }
  if (stage === 'queue') return BATCH_QUEUE_PROGRESS
  return safePercent
}

function assertSupportedCategory(category: ResourceCategoryKey): asserts category is 'graphic' | 'audio' | 'video' | 'font' {
  if (!['graphic', 'audio', 'video', 'font'].includes(category)) {
    throw new Error('当前分类暂不支持本地资源使用')
  }
}

function fileNameToObjectSegment(fileName: string): string {
  return sanitizeFileName(fileName).replace(/\.[^.]+$/i, '') || 'font'
}

async function loadFontForPreview(displayName: string, file: File): Promise<string> {
  if (typeof FontFace === 'undefined') {
    throw new Error('当前浏览器不支持字体预览，无法添加字体')
  }
  const family = `avgmaker-local-font-${Date.now()}-${Math.random().toString(16).slice(2)}`
  const objectUrl = URL.createObjectURL(file)
  try {
    const fontFace = new FontFace(family, `url(${objectUrl})`)
    await fontFace.load()
    document.fonts.add(fontFace)
    await document.fonts.load(`24px "${family}"`)
    return family
  } catch {
    throw new Error(`字体无法在预览中正常加载：${displayName}`)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

async function createFontPreviewBlob(params: {
  file: File
  displayName: string
}): Promise<Blob> {
  const family = await loadFontForPreview(params.displayName, params.file)
  const canvas = document.createElement('canvas')
  canvas.width = FONT_PREVIEW_SIZE
  canvas.height = FONT_PREVIEW_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('当前浏览器不支持字体预览图生成')
  }
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, FONT_PREVIEW_SIZE, FONT_PREVIEW_SIZE)
  ctx.fillStyle = '#111827'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `42px "${family}"`
  ctx.fillText(params.displayName, FONT_PREVIEW_SIZE / 2, 142)
  ctx.font = `36px "${family}"`
  ctx.fillText('橙光字体预览', FONT_PREVIEW_SIZE / 2, 244)
  ctx.font = `30px "${family}"`
  ctx.fillText('春风十里 ABC 123', FONT_PREVIEW_SIZE / 2, 338)
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
        return
      }
      reject(new Error('字体预览图生成失败'))
    }, 'image/png')
  })
}

/**
 * 本地文件是否允许在当前资源分类下执行“使用”。
 */
export function canApplyLocalResource(category: ResourceCategoryKey, fileName: string): boolean {
  const ext = getFileExtension(fileName)
  if (!ext) return false
  if (category === 'graphic') return IMAGE_EXTENSIONS.has(ext) || OUI_EXTENSIONS.has(ext)
  if (category === 'audio') return AUDIO_EXTENSIONS.has(ext)
  if (category === 'video') return VIDEO_EXTENSIONS.has(ext)
  if (category === 'font') return FONT_EXTENSIONS.has(ext)
  return false
}

async function persistVirtualRoots(params: {
  roots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>
  dataDirectoryHandle?: FileSystemDirectoryHandle | null
  project: MainProject
  ossUid?: number | string | null
}) {
  if (params.dataDirectoryHandle) {
    await writeVirtualResourcesToDataDir(params.dataDirectoryHandle, params.roots)
  }
  if (params.ossUid !== undefined && params.ossUid !== null && String(params.ossUid).trim()) {
    await uploadVirtualResourceRootsToOss({
      project: params.project,
      virtualResourceRoots: params.roots,
      uid: params.ossUid,
    })
  }
  await uploadVirtualResourcesJsonIfConfigured(params.roots)
}

/**
 * 批量导入本地图像 / 音频 / 视频资源：并发校验上传，最后一次性写入虚拟资源树。
 */
export async function applyLocalVirtualResources(params: {
  category: ResourceCategoryKey
  folderPath: string
  files: File[]
  project: MainProject
  sessionId: string
  currentRoot: DataFileMap | null
  allRoots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>
  dataDirectoryHandle?: FileSystemDirectoryHandle | null
  ossUid?: number | string | null
  onProgress?: LocalResourceApplyProgressCallback
}): Promise<{ root: DataFileMap; items: Array<{ file: File; url: string; folderSegments: string[] }> }> {
  assertSupportedCategory(params.category)
  if (params.category === 'font') {
    throw new Error('字体资源不走批量虚拟资源映射，请改用 applyLocalFontResource')
  }
  if (!params.files.length) {
    const root = params.currentRoot ?? buildDefaultVirtualResourceRoot(params.category)
    return { root, items: [] }
  }

  const guid = getProjectGuid(params.project)
  if (!guid) {
    throw new Error('当前工程缺少 guid，无法上传本地资源')
  }
  if (!params.sessionId.trim()) {
    throw new Error('当前登录态缺少 sessionId，无法上传本地资源')
  }

  const folderSegments = params.folderPath.split('/').filter(Boolean)
  const uploadItems = params.files.map((file) => {
    const displayFileName = sanitizeFileName(file.name) || file.name
    return {
      file,
      objectName: buildProjectResourceObjectName({
        guid,
        category: params.category,
        folderSegments,
        fileName: displayFileName,
      }),
      resultFileName: displayFileName,
    }
  })

  const uploaded = await uploadMaterialFiles({
    guid,
    sessionId: params.sessionId.trim(),
    version: getProjectVersion(params.project),
    items: uploadItems,
    onProgress: (stage, percent) => {
      const detail =
        stage === 'hashing'
          ? '校验资源'
          : stage === 'uploading'
            ? '检查/上传资源'
            : '等待资源入库'
      emitLocalApplyProgress(params.onProgress, {
        stage,
        percent: mapBatchUploadProgress(stage, percent),
        detail: `${detail} ${Math.min(params.files.length, Math.max(1, Math.ceil((percent / 100) * params.files.length)))}/${params.files.length}`,
      })
    },
  })

  let nextRoot = params.currentRoot
  for (const item of uploaded) {
    nextRoot = appendMaterialResourceMapping({
      root: nextRoot,
      category: params.category,
      folderSegments,
      fileName: item.fileName,
      url: item.url,
      md5: item.md5,
      size: item.size,
    })
  }

  emitLocalApplyProgress(params.onProgress, {
    stage: 'persisting',
    percent: 96,
    detail: `写入 ${uploaded.length} 个资源映射`,
  })
  await persistVirtualRoots({
    roots: {
      ...params.allRoots,
      [params.category]: nextRoot,
    },
    dataDirectoryHandle: params.dataDirectoryHandle,
    project: params.project,
    ossUid: params.ossUid,
  })
  emitLocalApplyProgress(params.onProgress, {
    stage: 'completed',
    percent: 100,
    detail: `导入完成 ${uploaded.length} 个资源`,
  })

  return {
    root: nextRoot ?? buildDefaultVirtualResourceRoot(params.category),
    items: uploaded.map((item, index) => ({
      file: params.files[index]!,
      url: item.url,
      folderSegments,
    })),
  }
}

/** 本地 .oui 皮肤：统一走公共皮肤加载模块。 */
export async function applyLocalOuiSkinResource(params: {
  file: File
  project: MainProject
  sessionId: string
  ossUid?: number | string | null
  roots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>
  dataDirectoryHandle?: FileSystemDirectoryHandle | null
  onProgress?: LocalResourceApplyProgressCallback
}): Promise<SkinApplyResult> {
  const guid = getProjectGuid(params.project)
  if (!guid) {
    throw new Error('当前工程缺少 guid，无法应用本地皮肤')
  }
  if (getFileExtension(params.file.name) !== 'oui') {
    throw new Error('当前文件不是 .oui 皮肤文件')
  }
  if (!params.sessionId.trim()) {
    throw new Error('当前登录态缺少 sessionId，无法应用本地皮肤')
  }

  const skinPackage = parseSkinOuiPackage({
    project: params.project,
    source: await BrowserFileUtils.blobToArrayBuffer(params.file),
    dataVersion: params.project.projectConstant.dataVersion || params.project.dataVer,
    onProgress: (percent, status) => {
      emitLocalApplyProgress(params.onProgress, {
        stage: 'parsing',
        percent: percent * 0.22,
        detail: status || `解析皮肤 ${params.file.name}`,
      })
    },
  })
  applySkinPackageToProject(params.project, skinPackage)

  const totalEntries = Math.max(1, skinPackage.entries.length)
  const applied = await applySkinResources({
    project: params.project,
    roots: params.roots as Record<ResourceCategoryKey, DataFileMap | null>,
    entries: skinPackage.entries,
    resolveResource: async (entry, index) => {
      const uploaded = await uploadMaterialFile({
        file: entry.file,
        guid,
        sessionId: params.sessionId.trim(),
        version: getProjectVersion(params.project),
        objectName: buildProjectResourceObjectName({
          guid,
          category: entry.category,
          folderSegments: entry.folderSegments,
          fileName: entry.fileName,
        }),
        resultFileName: entry.fileName,
        onProgress: (stage, percent) => {
          const mappedPercent = 22 + (((index + percent / 100) / totalEntries) * 70)
          emitLocalApplyProgress(params.onProgress, {
            stage,
            percent: mappedPercent,
            detail:
              stage === 'hashing'
                ? `计算皮肤资源校验 ${entry.fileName}`
                : stage === 'uploading'
                  ? `上传皮肤资源 ${entry.fileName}`
                  : `等待皮肤资源入库 ${entry.fileName}`,
          })
        },
      })
      return {
        resourceUrl: uploaded.url,
        fileName: uploaded.fileName,
        md5: uploaded.md5,
        size: uploaded.size,
      }
    },
  })
  validateImportedProjectResourcePaths(params.project, applied.roots.graphic)

  emitLocalApplyProgress(params.onProgress, {
    stage: 'persisting',
    percent: 96,
    detail: `写入皮肤资源映射 ${params.file.name}`,
  })
  await persistVirtualRoots({
    roots: {
      ...params.roots,
      ...applied.roots,
    },
    dataDirectoryHandle: params.dataDirectoryHandle,
    project: params.project,
    ossUid: params.ossUid,
  })
  emitLocalApplyProgress(params.onProgress, {
    stage: 'completed',
    percent: 100,
    detail: `皮肤导入完成 ${params.file.name}`,
  })
  return applied
}

/** 本地字体上传到服务器，并写入字体虚拟资源树。 */
export async function applyLocalFontResource(params: {
  file: File
  project: MainProject
  sessionId: string
  ossUid?: number | string | null
  folderPath?: string
  currentRoot: DataFileMap | null
  allRoots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>
  dataDirectoryHandle?: FileSystemDirectoryHandle | null
  onProgress?: LocalResourceApplyProgressCallback
}): Promise<{ fileName: string; objectKey: string; url: string; root: DataFileMap; folderSegments: string[] }> {
  const guid = getProjectGuid(params.project)
  if (!guid) {
    throw new Error('当前工程缺少 guid，无法上传本地字体')
  }
  if (!params.sessionId.trim()) {
    throw new Error('当前登录态缺少 sessionId，无法上传本地字体')
  }
  if (!canApplyLocalResource('font', params.file.name)) {
    throw new Error('当前文件不是支持的字体格式')
  }

  const fileName = sanitizeFileName(params.file.name) || 'font.ttf'
  const displayName = await resolveFontDisplayNameFromAsset(fileName, params.file)
  const version = getProjectVersion(params.project)
  const objectSegment = fileNameToObjectSegment(fileName)
  const fontObjectName = `${guid}/font/${fileName}`.toLowerCase()
  const previewFileName = `${objectSegment}_preview.png`

  const previewBlob = await createFontPreviewBlob({
    file: params.file,
    displayName,
  })
  const previewFile = new File([previewBlob], previewFileName, { type: 'image/png' })

  const uploadedFont = await uploadMaterialFile({
    file: params.file,
    guid,
    sessionId: params.sessionId.trim(),
    version,
    objectName: fontObjectName,
    resultFileName: fileName,
    onProgress: (stage, percent) => {
      emitLocalApplyProgress(params.onProgress, {
        stage,
        percent: percent * 0.7,
        detail: stage === 'hashing' ? `校验字体 ${fileName}` : `检查/上传字体 ${fileName}`,
      })
    },
  })

  const uploadedPreview = await uploadMaterialFile({
    file: previewFile,
    guid,
    sessionId: params.sessionId.trim(),
    version,
    objectName: `${guid}/font-preview/${previewFileName}`.toLowerCase(),
    resultFileName: previewFileName,
    onProgress: (stage, percent) => {
      emitLocalApplyProgress(params.onProgress, {
        stage,
        percent: 70 + percent * 0.2,
        detail: stage === 'hashing' ? `校验字体预览图 ${fileName}` : `检查/上传字体预览图 ${fileName}`,
      })
    },
  })

  const folderSegments = params.folderPath?.split('/').filter(Boolean).length
    ? params.folderPath.split('/').filter(Boolean)
    : [getResourceCategoryRule('font').topFolders[1]!.name]
  const nextRoot = appendMaterialResourceMapping({
    root: params.currentRoot,
    category: 'font',
    folderSegments,
    fileName,
    url: uploadedFont.url,
    md5: uploadedFont.md5,
    size: uploadedFont.size,
    extraLinks: {
      [FONT_DISPLAY_NAME_LINK_INDEX]: displayName,
      [FONT_PREVIEW_MD5_LINK_INDEX]: uploadedPreview.md5,
    },
  })

  emitLocalApplyProgress(params.onProgress, {
    stage: 'persisting',
    percent: 96,
    detail: `写入字体映射 ${fileName}`,
  })
  await persistVirtualRoots({
    roots: {
      ...params.allRoots,
      font: nextRoot,
    },
    dataDirectoryHandle: params.dataDirectoryHandle,
    project: params.project,
    ossUid: params.ossUid,
  })
  emitLocalApplyProgress(params.onProgress, {
    stage: 'completed',
    percent: 100,
    detail: `导入完成 ${fileName}`,
  })

  return {
    fileName,
    objectKey: fontObjectName,
    url: uploadedFont.url,
    root: nextRoot,
    folderSegments,
  }
}

